import { useCallback, useEffect, useRef, useState } from "react";
import Daily from "@daily-co/daily-js";

const friendly = (e) => {
  const m = e?.error?.type || e?.errorMsg || e?.message || "";
  if (/payment-method|plan/i.test(m)) return "The studio's video account needs a card on file before anyone can join (Daily dashboard → Billing).";
  return typeof m === "string" && m ? m : "The studio connection failed.";
};

// Joins a Daily room with a server-issued token and keeps a live participant map.
export function useDailyCall({ url, token }) {
  const ref = useRef(null);
  const [participants, setParticipants] = useState({});
  const [state, setState] = useState("idle"); // idle | joining | joined | left | error
  const [error, setError] = useState("");

  const refresh = useCallback(() => {
    if (ref.current) setParticipants({ ...ref.current.participants() });
  }, []);

  useEffect(() => {
    if (!url || !token) return undefined;
    let live = true;
    let call = null;
    let timer = null;
    // Deferred so React StrictMode's mount→unmount→mount never creates two calls.
    const boot = setTimeout(() => {
      const prior = Daily.getCallInstance && Daily.getCallInstance();
      if (prior) prior.destroy();
      call = Daily.createCallObject({ subscribeToTracksAutomatically: true });
      ref.current = call;
      const evts = ["participant-joined", "participant-updated", "participant-left", "track-started", "track-stopped", "joined-meeting"];
      evts.forEach((e) => call.on(e, refresh));
      call.on("left-meeting", () => live && setState("left"));
      call.on("error", (e) => { if (!live) return; setError(friendly(e)); setState("error"); });
      call.on("meeting-state-changed", (e) => {
        if (live && e?.meetingState === "error") { setError("The video service refused the connection. On a new Daily account, add a card on file first (Daily dashboard → Billing)."); setState("error"); }
      });
      call.on("camera-error", (e) => live && setError(e?.errorMsg?.errorMsg || "Camera or microphone is blocked. Allow access in your browser settings."));
      setState("joining");
      call.join({ url, token })
        .then(() => { if (live) { setState("joined"); refresh(); } })
        .catch((e) => { if (live) { setError(friendly(e)); setState("error"); } });
      timer = setTimeout(() => {
        if (!live || call.meetingState() === "joined-meeting") return;
        setError(call.meetingState() === "error"
          ? "The video service refused the connection. On a new Daily account, add a card on file first (Daily dashboard → Billing)."
          : "Couldn't connect to the studio. Check your connection and try again.");
        setState("error");
      }, 15000);
    }, 0);
    return () => { live = false; clearTimeout(boot); clearTimeout(timer); if (call) call.destroy(); if (ref.current === call) ref.current = null; };
  }, [url, token, refresh]);

  return { call: ref.current, callRef: ref, participants, state, error };
}
